import type { FC } from 'react'
import { CircularProgress, Stack, Typography } from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import type { LuciMessage } from './LuciService'

type TraceStatus = 'started' | 'finished' | 'error'

export interface TraceEntry {
  tool: string
  label: string
  status: TraceStatus
}

const TRACE_STATUSES: TraceStatus[] = ['started', 'finished', 'error']

/** The tool calls a turn recorded, in call order; the column is untyped JSON, so each is checked. */
export const traceOf = (message: LuciMessage): TraceEntry[] =>
  Array.isArray(message.tool_trace) ? message.tool_trace.filter(isEntry) : []

const isEntry = (value: unknown): value is TraceEntry => {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.tool === 'string' &&
    typeof candidate.label === 'string' &&
    TRACE_STATUSES.includes(candidate.status as TraceStatus)
  )
}

interface ToolTraceProps {
  entries: TraceEntry[]
  settled: boolean
}

/**
 * One line per tool the turn called, live or replayed from the stored trace alike; a live turn
 * whose tools have all reported is composing its answer, and says so.
 */
export const ToolTrace: FC<ToolTraceProps> = ({ entries, settled }) => {
  if (entries.length === 0) return null
  const composing = !settled && entries.every(entry => entry.status !== 'started')
  return (
    <Stack spacing={0.5} sx={{ mb: 1 }} data-testid="luci-trace">
      {entries.map((entry, index) => (
        <Stack
          key={index}
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center' }}
          data-testid={`luci-trace-${entry.tool}`}
          data-status={entry.status}
        >
          <TraceIcon status={entry.status} settled={settled} />
          <Typography sx={{ color: 'text.secondary', fontSize: '1.3rem' }}>
            {entry.label}
          </Typography>
        </Stack>
      ))}
      {composing && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center' }}
          data-testid="luci-composing"
        >
          <CircularProgress size={14} />
          <Typography sx={{ color: 'text.secondary', fontSize: '1.3rem' }}>
            Composing the answer…
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}

// A tool still "started" on a settled turn never reported back: the run ended around it.
const TraceIcon: FC<{ status: TraceStatus; settled: boolean }> = ({ status, settled }) => {
  if (status === 'finished') {
    return <CheckRoundedIcon fontSize="small" sx={{ color: 'success.main' }} />
  }
  if (status === 'error' || settled) {
    return <WarningAmberRoundedIcon fontSize="small" sx={{ color: 'warning.main' }} />
  }
  return <CircularProgress size={14} />
}
